import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { RefreshCw, Phone, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { adminOfficesApi, errMsg } from './adminOfficesApi';
import { useConfirm } from '../useConfirm';

const STATUS = { new: ['New', 'bg-sky-100 text-sky-800'], called: ['Called', 'bg-amber-100 text-amber-800'], closed: ['Closed', 'bg-slate-200 text-slate-700'] };

const InquiryRow = ({ r, api, reload, confirm }) => {
  const [notes, setNotes] = useState(r.notes || '');
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const [label, cls] = STATUS[r.status] || STATUS.new;
  return (
    <div className="p-4 space-y-3" data-testid={`inquiry-row-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{r.dso_name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${cls}`} data-testid={`inquiry-status-${r.id}`}>{label}</span></p>
          <p className="text-sm text-slate-600">{r.contact_name} · {r.locations} locations · <a className="text-blue-700" href={`tel:${r.phone}`}><Phone className="inline w-3.5 h-3.5" /> {r.phone}</a>{r.email ? ` · ${r.email}` : ''}</p>
          <p className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}{r.called_at ? ` · called ${new Date(r.called_at).toLocaleDateString()}` : ''}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {['new', 'called', 'closed'].filter((s) => s !== r.status).map((s) => (
            <Button key={s} size="sm" variant="outline" data-testid={`inquiry-mark-${s}-${r.id}`}
              onClick={() => act(() => api.updateInquiry(r.id, { status: s }), `Marked ${STATUS[s][0].toLowerCase()}`)}>Mark {STATUS[s][0].toLowerCase()}</Button>
          ))}
          <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`inquiry-delete-${r.id}`}
            onClick={async () => (await confirm({ title: 'Delete this inquiry?', ok: 'Delete', danger: true })) && act(() => api.deleteInquiry(r.id), 'Inquiry deleted')}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Textarea rows={2} placeholder="Call notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`inquiry-notes-${r.id}`} />
        <Button size="sm" variant="outline" disabled={notes === (r.notes || '')} data-testid={`inquiry-save-notes-${r.id}`}
          onClick={() => act(() => api.updateInquiry(r.id, { notes }), 'Notes saved')}>Save</Button>
      </div>
    </div>
  );
};

export const EnterpriseInquiriesAdmin = ({ adminToken }) => {
  const api = useMemo(() => adminOfficesApi(adminToken), [adminToken]);
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState('all');
  const [confirmDialog, confirm] = useConfirm();
  const reload = useCallback(async () => { try { setD(await api.inquiries()); } catch (e) { toast.error(errMsg(e)); } }, [api]);
  useEffect(() => { reload(); }, [reload]);
  const rows = (d?.inquiries || []).filter((r) => filter === 'all' || r.status === filter);
  return (
    <div className="space-y-6" data-testid="enterprise-inquiries-admin">
      <Toaster richColors position="top-right" />
      {confirmDialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Enterprise Inquiries</h2>
          <p className="text-sm text-slate-500">DSOs who used "Request info" on the pricing page. Each one is also emailed to admin@practcom.com.</p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} data-testid="inquiries-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[['all', 'All', d?.inquiries?.length], ['new', 'New', d?.counts?.new], ['called', 'Called', d?.counts?.called], ['closed', 'Closed', d?.counts?.closed]].map(([k, l, n]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`inquiries-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white'}`}>{l} ({n || 0})</button>
        ))}
      </div>
      <div className="bg-white border rounded-xl divide-y" data-testid="inquiries-list">
        {!d ? <p className="p-6 text-slate-500">Loading…</p> : !rows.length ? <p className="p-6 text-slate-500" data-testid="inquiries-empty">No inquiries here yet.</p>
          : rows.map((r) => <InquiryRow key={`${r.id}-${r.updated_at || ''}`} r={r} api={api} reload={reload} confirm={confirm} />)}
      </div>
    </div>
  );
};
